import React, { useState } from 'react';
import './DatasetUsage.css';
import { auth } from '../../backend/firebase';
import { FaCopy, FaCheck } from 'react-icons/fa';

const DatasetUsage = ({ datasetId }) => {
  const [activeTab, setActiveTab] = useState('node');
  const [copiedIndex, setCopiedIndex] = useState(null);

  const userUid = auth.currentUser?.uid || "<YOUR_USER_ID>";

  const handleCopy = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getCodeContent = () => {
    if (activeTab === 'node') {
      return {
        install: 'npm install @seta-dot/datasets',
        usageRecent: `const { getRecentSeta } = require('@seta-dot/datasets');

// Fetch latest version
getRecentSeta({
  id: "${datasetId}",
  userId: "${userUid}",
  savePath: "./dataset-data"
});`,
        usageVersion: `const { getSetaByVersion } = require('@seta-dot/datasets');

// Fetch specific version
getSetaByVersion({
  id: "${datasetId}",
  version: "1.0.0",
  userId: "${userUid}",
  savePath: "./dataset-data/v1"
});`,
        usageInstance: `const { getSetaInstance } = require('@seta-dot/datasets');

// Fetch specific instance
getSetaInstance({
  id: "${datasetId}",
  version: "1.0.0",
  instanceId: "<INSTANCE_ID>",
  userId: "${userUid}",
  savePath: "./dataset-data/run-55"
});`
      };
    } else {
      return {
        install: 'pip install seta-datasets',
        usageRecent: `from seta import get_recent_seta

# Fetch latest version
get_recent_seta(
    dataset_id="${datasetId}",
    user_id="${userUid}",
    save_path="./dataset-data"
)`,
        usageVersion: `from seta import get_seta_by_version

# Fetch specific version
get_seta_by_version(
    dataset_id="${datasetId}",
    version="1.0.0",
    user_id="${userUid}",
    save_path="./dataset-data/v1"
)`,
        usageInstance: `from seta import get_seta_instance

# Fetch specific instance
get_seta_instance(
    dataset_id="${datasetId}",
    version="1.0.0",
    instance_id="<INSTANCE_ID>",
    user_id="${userUid}",
    save_path="./dataset-data/run-55"
)`
      };
    }
  };

  const content = getCodeContent();

  return (
    <div className="seta-dataset-usage">
      <div className="seta-usage-header">
        <h2 className="seta-usage-title">Integration Guide</h2>
        <div className="seta-tab-container">
          <button 
            className={`seta-tab-btn ${activeTab === 'node' ? 'seta-tab-active' : ''}`}
            onClick={() => setActiveTab('node')}
          >
            Node
          </button>
          <button 
            className={`seta-tab-btn ${activeTab === 'python' ? 'seta-tab-active' : ''}`}
            onClick={() => setActiveTab('python')}
          >
            Python
          </button>
        </div>
      </div>

      <div className="seta-usage-content">
        {/* Installation */}
        <div className="seta-usage-card">
          <div className="seta-card-header">
            <span className="seta-card-number">01</span>
            <h3 className="seta-card-title">Installation</h3>
          </div>
          <p className="seta-card-desc">Add the package to your project</p>
          <SetaCodeBlock 
            code={content.install}
            language="bash"
            onCopy={() => handleCopy(content.install, 0)}
            copied={copiedIndex === 0}
          />
        </div>

        {/* Method 1 */}
        <div className="seta-usage-card">
          <div className="seta-card-header">
            <span className="seta-card-number">02</span>
            <h3 className="seta-card-title">Fetch Latest Data</h3>
          </div>
          <p className="seta-card-desc">
            Download the most recent version of this dataset
          </p>
          <SetaCodeBlock 
            code={content.usageRecent}
            language={activeTab === 'node' ? 'javascript' : 'python'}
            onCopy={() => handleCopy(content.usageRecent, 1)}
            copied={copiedIndex === 1}
          />
        </div>

        {/* Method 2 */}
        <div className="seta-usage-card">
          <div className="seta-card-header">
            <span className="seta-card-number">03</span>
            <h3 className="seta-card-title">Fetch by Version</h3>
          </div>
          <p className="seta-card-desc">
            Lock to a specific semantic version for reproducibility
          </p>
          <SetaCodeBlock 
            code={content.usageVersion}
            language={activeTab === 'node' ? 'javascript' : 'python'}
            onCopy={() => handleCopy(content.usageVersion, 2)}
            copied={copiedIndex === 2}
          />
        </div>

        {/* Method 3 */}
        <div className="seta-usage-card">
          <div className="seta-card-header">
            <span className="seta-card-number">04</span>
            <h3 className="seta-card-title">Fetch Specific Snapshot</h3>
          </div>
          <p className="seta-card-desc">
            Retrieve an immutable snapshot from the Explore Table
          </p>
          <SetaCodeBlock 
            code={content.usageInstance}
            language={activeTab === 'node' ? 'javascript' : 'python'}
            onCopy={() => handleCopy(content.usageInstance, 3)}
            copied={copiedIndex === 3}
          />
        </div>
      </div>
    </div>
  );
};

const SetaCodeBlock = ({ code, language, onCopy, copied }) => (
  <div className="seta-code-block">
    <div className="seta-code-header">
      <span className="seta-lang-label">{language}</span>
      <button 
        className={`seta-copy-btn ${copied ? 'seta-copied' : ''}`} 
        onClick={onCopy}
        aria-label="Copy code"
      >
        {copied ? (
          <>
            <FaCheck className="seta-copy-icon" /> Copied
          </>
        ) : (
          <>
            <FaCopy className="seta-copy-icon" /> Copy
          </>
        )}
      </button>
    </div>
    <pre className="seta-code-content">
      <code>{code}</code>
    </pre>
  </div>
);

export default DatasetUsage;